// ============================================================================
// EDUSPHERE ENTERPRISE - AXIOS HTTP CLIENT
// Configured with JWT token management, branch/academic year context headers,
// and automatic 401 token refresh with request queuing.
// ============================================================================

import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '@/store';

const API_BASE_URL = import.meta.env.VITE_API_URL;

if (!API_BASE_URL) {
  throw new Error('VITE_API_URL environment variable is required. Check your .env file.');
}

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
  validateStatus: (status) => (status >= 200 && status < 300) || status === 304,
  // Auth lives in httpOnly cookies now, not a header we attach manually —
  // the browser sends them automatically as long as requests are
  // same-origin (dev: via the Vite proxy; prod: via nginx).
  withCredentials: true,
  // Double-submit CSRF: axios reads this cookie and echoes it back as the
  // named header automatically on same-origin requests.
  xsrfCookieName: 'XSRF-TOKEN',
  xsrfHeaderName: 'X-XSRF-TOKEN',
});

const toCamelCase = (key: string): string => key.replace(/_([a-z])/g, (_, letter: string) => letter.toUpperCase());

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  if (!value || typeof value !== 'object') return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
};

const addCamelCaseAliases = (input: unknown): unknown => {
  if (Array.isArray(input)) {
    return input.map((item) => addCamelCaseAliases(item));
  }

  if (!isPlainObject(input)) {
    return input;
  }

  const output: Record<string, unknown> = {};
  Object.entries(input).forEach(([key, value]) => {
    const normalizedValue = addCamelCaseAliases(value);
    output[key] = normalizedValue;

    const camelKey = toCamelCase(key);
    if (camelKey !== key && !(camelKey in output)) {
      output[camelKey] = normalizedValue;
    }
  });

  return output;
};

const responseCache = new Map<string, unknown>();

const buildRequestCacheKey = (config?: InternalAxiosRequestConfig): string | null => {
  if (!config) return null;

  const method = (config.method || 'get').toLowerCase();
  const url = config.url || '';
  const params = config.params
    ? JSON.stringify(
      Object.keys(config.params)
        .sort()
        .reduce<Record<string, unknown>>((acc, key) => {
          acc[key] = config.params[key];
          return acc;
        }, {}),
    )
    : '';

  return `${method}:${url}?${params}`;
};

// ============================================================================
// REQUEST INTERCEPTOR
// Attaches X-Branch-Id and X-Academic-Year-Id from localStorage. Auth itself
// travels via the httpOnly cookie set by the server — nothing to attach here.
// ============================================================================
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const branchId = localStorage.getItem('branchId');
    if (branchId) {
      config.headers['X-Branch-Id'] = branchId;
    }

    const academicYearId = localStorage.getItem('academicYearId');
    if (academicYearId) {
      config.headers['X-Academic-Year-Id'] = academicYearId;
    }

    return config;
  },
  (error) => Promise.reject(error),
);

// ============================================================================
// RESPONSE INTERCEPTOR
// Handles 401 responses by attempting a token refresh via /auth/refresh,
// then retrying the original request. Concurrent 401s are queued so only
// one refresh request is made at a time.
// ============================================================================
let isRefreshing = false;
let failedQueue: { resolve: () => void; reject: (reason?: any) => void }[] = [];

const processQueue = (error: any) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve();
    }
  });
  failedQueue = [];
};

apiClient.interceptors.response.use(
  (response) => {
    const method = (response.config?.method || 'get').toLowerCase();
    const cacheKey = buildRequestCacheKey(response.config);

    if (method === 'get' && response.status === 304 && cacheKey && responseCache.has(cacheKey)) {
      response.data = responseCache.get(cacheKey);
      return response;
    }

    if (method === 'get' && response.status === 304) {
      const requestConfig = response.config as (InternalAxiosRequestConfig & { _bypassCache?: boolean });
      if (!requestConfig._bypassCache) {
        const retryConfig = {
          ...requestConfig,
          params: {
            ...(requestConfig.params as Record<string, unknown> || {}),
            _cb: Date.now(),
          },
        } as InternalAxiosRequestConfig & { _bypassCache?: boolean };

        retryConfig._bypassCache = true;
        return apiClient.request(retryConfig);
      }
    }

    const normalizedData = addCamelCaseAliases(response.data);
    response.data = normalizedData;

    if (method === 'get' && response.status !== 304 && cacheKey) {
      responseCache.set(cacheKey, normalizedData);
    }

    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // A response with no `error.response` means the request never got a
    // reply at all — a network drop, timeout, or (very commonly in dev) the
    // API process mid-restart under `tsx watch`. That is NOT the server
    // telling us the session is invalid, so it must never be treated the
    // same as an explicit 401 — doing so was forcing a full logout/redirect
    // to the login page every time the dev API restarted, even though the
    // session cookies were still perfectly valid. Just surface the error.
    if (!error.response) {
      return Promise.reject(error);
    }

    // Only attempt refresh on 401 and only once per request
    if (error.response.status === 401 && !originalRequest._retry) {
      // If a refresh is already in progress, queue this request
      if (isRefreshing) {
        return new Promise<void>((resolve, reject) => {
          failedQueue.push({ resolve: () => resolve(), reject });
        }).then(() => apiClient(originalRequest));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Use a raw axios call (not apiClient) to avoid interceptor loops.
        // No body needed — the refresh token travels via its own httpOnly
        // cookie, scoped to /api/v1/auth, and is sent automatically.
        await axios.post(`${API_BASE_URL}/auth/refresh`, {}, { withCredentials: true });

        // The server rotated both cookies via Set-Cookie; nothing to store
        // client-side. Retry the original (and queued) requests as-is.
        processQueue(null);
        return apiClient(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError);
        // The server explicitly rejected the refresh (expired/revoked
        // refresh token, or — on the very first load of a fresh session —
        // simply no session at all yet). Clear the client-side auth state;
        // ProtectedRoute/PublicRoute are subscribed to this same store and
        // will react immediately with a normal React Router redirect.
        //
        // Deliberately NOT a `window.location.href` hard reload: this
        // interceptor runs again on every fresh page load (bootstrapAuth's
        // GET /auth/me triggers it whenever there's no session yet), so a
        // hard reload here reloads /auth/login while already on
        // /auth/login — which still counts as a navigation and re-runs
        // main.tsx from scratch, calling bootstrapAuth again, hitting this
        // same catch block again, forever. That was the "auth page keeps
        // refreshing itself" loop.
        localStorage.removeItem('branchId');
        localStorage.removeItem('academicYearId');
        useAuthStore.getState().logout();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  },
);

export default apiClient;
